/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DenseViewBox.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React from "react";
import { Box, Typography, Card } from "@mui/material";
import CustomButtonV2 from "../common/button/CustomButtonV2";
import dayjs from "dayjs";
import ImageBox from "./ImageBox";
import RefreshIcon from "@mui/icons-material/Refresh";
import CustomToolTip from "../../infoButton/CustomToolTip";
import { componentStyle } from "../../../config/appTheme";

function DenseViewBox({
  imageUrl,
  title,
  onRecreateClick,
  vehicleClass,
  projectOwner,
  trademark,
  vehiclePlatform,
  createdAt,
  updatedAt,
  status,
  architectureName,
  onViewMore,
}) {
  return (
    <Card
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        boxShadow: 2,
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ display: "flex", gap: 1 }}
        >
          <Box sx={{ mb: -1, cursor: "pointer" }} onClick={onRecreateClick}>
            <RefreshIcon />
          </Box>

          <CustomToolTip
            type="string"
            length={50}
            style={{
              ...componentStyle.font.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",

            }}
            title={`Project Title: ${title || "N/A"}`}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {(title || "UNKNOWN").slice(0, 30).toUpperCase()}
              {title?.length > 30 ? "..." : ""} - {architectureName}
            </Typography>
          </CustomToolTip>
        </Typography>
        <Typography variant="subtitle2" fontWeight="bold">
          {status || "ACTIVE PRE SOP"}
        </Typography>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          display: "flex",

          justifyContent: "space-between",
          gap: 2,
        }}
      >
        {/* Image */}
        <ImageBox boxes={imageUrl} />

        {/* Details */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.3,
          }}
        >
          <Typography
            textAlign={"start"}
            variant="body2"
            color="text.secondary"
          >
            <strong>Vehicle Class:</strong> {vehicleClass || "N/A"}
          </Typography>
          <Typography
            textAlign={"start"}
            variant="body2"
            color="text.secondary"
          >
            <strong>Project Owner:</strong> {projectOwner || "N/A"}
          </Typography>
          <CustomToolTip
            type="string"
            length={50}
            style={{
              ...componentStyle.font.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",

            }}
            title={`Trademark: ${trademark || "N/A"}`}
          >
            <Typography
              textAlign={"start"}
              variant="body2"
              color="text.secondary"
            >
              <strong>Trademark:</strong> {trademark || "N/A"}
            </Typography>
          </CustomToolTip>

          <CustomToolTip
            type="string"
            length={50}
            style={{
              ...componentStyle.font.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",

            }}
            title={`Vehicle Platform: ${vehiclePlatform || "N/A"}`}
          >
            <Typography
              textAlign={"start"}
              variant="body2"
              color="text.secondary"
            >
              <strong>Vehicle Platform:</strong> {vehiclePlatform || "N/A"}
            </Typography>
          </CustomToolTip>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          mt: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Created At:{" "}
          {createdAt ? dayjs(createdAt).format("DD MMM YYYY, hh:mm A") : "N/A"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Updated At:{" "}
          {updatedAt ? dayjs(updatedAt).format("DD MMM YYYY, hh:mm A") : "N/A"}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
          <CustomButtonV2
            type="submit"
            btnName="View More"
            handleOnClick={onViewMore}
          />
        </Box>
      </Box>
    </Card>
  );
}

export default DenseViewBox;
